import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useTransportQuery } from "@/transport"
import { Button } from "@coval/ui"

import type {
  AgentJudgeEvidenceDisplayResource,
  AgentJudgeMetricOutputEvidenceDisplay,
  GetAgentJudgeEvidenceResponse,
} from "@/types_api/backend/transport/get_agent_judge_evidence_response"

export function AgentJudgeEvidence({
  metricOutputUlid,
  fallbackReferences = [],
}: {
  metricOutputUlid: string
  fallbackReferences?: Array<{ key: string; label: string }>
}) {
  const request = useMemo(
    () => ({ metric_output_ulid: metricOutputUlid }),
    [metricOutputUlid],
  )
  const query = useTransportQuery<
    "getAgentJudgeEvidence",
    GetAgentJudgeEvidenceResponse
  >({
    operation: "getAgentJudgeEvidence",
    request,
    options: CACHE_PROFILES.lookup,
  })

  return (
    <AgentJudgeEvidenceContent
      evidence={query.data?.evidence ?? []}
      fallbackReferences={fallbackReferences}
      isError={query.isError}
      isLoading={query.isLoading}
      onRetry={() => void query.refetch()}
    />
  )
}

interface AgentJudgeEvidenceContentProps {
  evidence: AgentJudgeEvidenceDisplayResource[]
  fallbackReferences?: Array<{ key: string; label: string }>
  isError: boolean
  isLoading: boolean
  onRetry: () => void
}

export function AgentJudgeEvidenceContent({
  evidence,
  fallbackReferences = [],
  isError,
  isLoading,
  onRetry,
}: AgentJudgeEvidenceContentProps) {
  const resolvedKeys = new Set(evidence.map(evidenceKey))
  const missingReferences = fallbackReferences.filter(
    (reference) => !resolvedKeys.has(reference.key),
  )
  const fallbackList = missingReferences.length > 0 && (
    <ul className="space-y-1 text-sm text-coval-text">
      {missingReferences.map((reference) => (
        <li key={reference.key}>{reference.label}</li>
      ))}
    </ul>
  )
  if (isError && evidence.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 text-sm text-coval-danger-text">
          <span>Evidence could not be loaded.</span>
          <Button type="button" variant="ghost" onClick={onRetry}>
            Retry
          </Button>
        </div>
        {fallbackList}
      </div>
    )
  }
  if (isLoading && evidence.length === 0) {
    return <p className="text-sm text-coval-text-muted">Loading evidence…</p>
  }
  if (evidence.length === 0) {
    return (
      fallbackList || (
        <p className="text-sm text-coval-text-muted">No evidence cited.</p>
      )
    )
  }

  return (
    <div>
      {isError && (
        <Button type="button" variant="ghost" onClick={onRetry}>
          Retry evidence
        </Button>
      )}
      <ul className="space-y-2 text-sm text-coval-text">
        {evidence.map((item, index) => (
          <li key={evidenceKey(item, index)}>{renderEvidence(item)}</li>
        ))}
      </ul>
      {fallbackList}
    </div>
  )
}

function evidenceKey(
  item: AgentJudgeEvidenceDisplayResource,
  index?: number,
): string {
  const reference = item.reference
  if ("metric_output_ulid" in reference) {
    return `metric:${reference.metric_output_ulid}`
  }
  if ("frame_id" in reference) {
    return `frame:${reference.frame_id}`
  }
  if ("trace_id" in reference) {
    return `trace:${reference.trace_id}:${reference.span_id}`
  }
  return `evidence:${index}`
}

function renderEvidence(item: AgentJudgeEvidenceDisplayResource) {
  const reference = item.reference
  if ("metric_output_ulid" in reference) {
    if (item.state === "resolved" && isMetricOutputDisplay(item.display)) {
      return <MetricOutputEvidence display={item.display} />
    }
    return (
      <span>Metric output unavailable ({reference.metric_output_ulid})</span>
    )
  }
  if ("frame_id" in reference) {
    return <span>Frame: {reference.frame_id}</span>
  }
  if ("trace_id" in reference) {
    return (
      <span>
        Trace span: {reference.trace_id} / {reference.span_id}
      </span>
    )
  }
  return <span>Unknown evidence reference</span>
}

function isMetricOutputDisplay(
  display: AgentJudgeEvidenceDisplayResource["display"],
): display is AgentJudgeMetricOutputEvidenceDisplay {
  return (
    typeof display === "object" &&
    display !== null &&
    "metric_name" in display &&
    "output_type" in display
  )
}

function MetricOutputEvidence({
  display,
}: {
  display: AgentJudgeMetricOutputEvidenceDisplay
}) {
  const value =
    display.output_type === "float"
      ? display.float_value
      : display.output_type === "set"
        ? display.set_value?.join(", ")
        : display.string_value
  const target = display.target_condition
    ? formatTarget(display.target_condition)
    : null

  return (
    <div className="rounded-md border border-coval-border bg-surface-default p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{display.metric_name}</span>
        {display.metric_version_number != null && (
          <span className="text-xs text-coval-text-muted">
            Version {display.metric_version_number}
          </span>
        )}
      </div>
      <p className="mt-1">
        {value == null || value === "" ? "No value" : String(value)}
        {display.unit ? ` ${display.unit}` : ""}
      </p>
      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-coval-text-muted">
        {display.status && <span>Status: {display.status}</span>}
        {target && <span>Target: {target}</span>}
        <span>{new Date(display.created_at).toLocaleString()}</span>
      </div>
    </div>
  )
}

function formatTarget(
  target: NonNullable<
    AgentJudgeMetricOutputEvidenceDisplay["target_condition"]
  >,
): string {
  const targetValue =
    target.target_values?.join(", ") ?? target.target_float ?? "—"
  return `${target.comparison_operator} ${targetValue}`
}
